import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  Columns,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { showDeleteConfirm, showSuccess } from '@/lib/swal'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { warehouseService } from '@/services/warehouseService'
import { customersService } from '@/services/customersService'
import { employeesService } from '@/services/employeesService'
import type { CityLookup, CountryLookup, Employee } from '@/api/types'
import type { Warehouse } from '../types'

const PAGE_SIZE = 10

type ColumnKey = 'code' | 'name' | 'location' | 'manager' | 'email' | 'contact' | 'city' | 'country'

const ALL_COLUMNS: { key: ColumnKey; labelKey: string }[] = [
  { key: 'code', labelKey: 'inventoryWarehouses.columns.code' },
  { key: 'name', labelKey: 'inventoryWarehouses.columns.name' },
  { key: 'location', labelKey: 'inventoryWarehouses.columns.location' },
  { key: 'manager', labelKey: 'inventoryWarehouses.columns.manager' },
  { key: 'email', labelKey: 'inventoryWarehouses.columns.email' },
  { key: 'contact', labelKey: 'inventoryWarehouses.columns.contact' },
  { key: 'city', labelKey: 'inventoryWarehouses.columns.city' },
  { key: 'country', labelKey: 'inventoryWarehouses.columns.country' },
]

export const InventoryWarehouseList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const branchId = user?.selectedBranch?.branchId

  const [rows, setRows] = useState<Warehouse[]>([])
  const [loading, setLoading] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [countries, setCountries] = useState<CountryLookup[]>([])
  const [cityById, setCityById] = useState<Record<number, CityLookup>>({})
  const fetchedEmployeesRef = React.useRef<Set<number>>(new Set())
  const employeeCacheRef = React.useRef<Record<number, Employee>>({})

  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
    code: true,
    name: true,
    location: true,
    manager: true,
    email: true,
    contact: true,
    city: true,
    country: true,
  })

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await customersService.getCountries()
        setCountries(data)
      } catch {
        setCountries([])
      }
    }
    void loadCountries()
  }, [])

  useEffect(() => {
    if (!branchId) return

    const loadWarehouses = async () => {
      try {
        setLoading(true)
        const list = await warehouseService.getAllWarehouses(branchId)
        setRows(list)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setRows([])
        if (!isNoData) {
          toast.error(error.response?.data?.message || t('inventoryWarehouses.failedToLoad'))
        }
      } finally {
        setLoading(false)
      }
    }

    void loadWarehouses()
  }, [branchId, t])

  const getEmployeeFullName = (e: Employee) => {
    if (language === 'ar') {
      return [e.empFirstNameAr, e.empSecondNameAr, e.empLastNameAr].filter(Boolean).join(' ').trim()
    }
    return [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
  }

  // Enrich list data with employee name/email/contact based on returned `headEmpId`/`empId`.
  useEffect(() => {
    if (!branchId) return
    if (rows.length === 0) return

    const uniqueEmpIds = Array.from(new Set(rows.map((r) => Number(r.empId)).filter((id) => id > 0)))
    const missing = uniqueEmpIds.filter((id) => !fetchedEmployeesRef.current.has(id))
    if (missing.length === 0) return

    let cancelled = false
    const loadEmployees = async () => {
      try {
        const results = await Promise.all(
          missing.map(async (empId) => {
            try {
              const emp = await employeesService.getEmployeeById(branchId, empId)
              return { empId, emp }
            } catch {
              return { empId, emp: null as Employee | null }
            }
          })
        )

        if (cancelled) return
        const nextCache: Record<number, Employee> = { ...employeeCacheRef.current }
        const nextFetched = new Set(fetchedEmployeesRef.current)

        for (const r of results) {
          if (r.emp) nextCache[r.empId] = r.emp
          nextFetched.add(r.empId)
        }

        employeeCacheRef.current = nextCache
        fetchedEmployeesRef.current = nextFetched

        // Update rows for the loaded employees.
        setRows((prev) =>
          prev.map((w) => {
            const emp = nextCache[w.empId]
            if (!emp) return w
            return {
              ...w,
              wareHouseManagerName: getEmployeeFullName(emp),
              email: String(emp.empEmail ?? ''),
              contactNumber: String(emp.empMobileNo ?? ''),
            }
          })
        )
      } finally {
        // no-op
      }
    }

    void loadEmployees()
    return () => {
      cancelled = true
    }
  }, [rows, branchId, language])

  // If language changes, recompute employee full name for already-enriched rows.
  useEffect(() => {
    if (rows.length === 0) return
    setRows((prev) =>
      prev.map((w) => {
        const emp = employeeCacheRef.current[w.empId]
        if (!emp) return w
        return { ...w, wareHouseManagerName: getEmployeeFullName(emp) }
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language])

  const uniqueCountryIdsForCities = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.country).filter((x) => x && x > 0))).sort((a, b) => a - b),
    [rows]
  )

  useEffect(() => {
    if (uniqueCountryIdsForCities.length === 0) {
      setCityById({})
      return
    }

    let cancelled = false

    const loadCities = async () => {
      try {
        const cityPairs = await Promise.all(
          uniqueCountryIdsForCities.map(async (cid) => {
            const cities = await customersService.getCities(cid)
            return cities
          })
        )
        if (cancelled) return
        const flat = cityPairs.flat()
        const map: Record<number, CityLookup> = {}
        flat.forEach((c) => {
          if (c?.id) map[c.id] = c
        })
        setCityById(map)
      } catch {
        if (!cancelled) setCityById({})
      }
    }

    void loadCities()
    return () => {
      cancelled = true
    }
  }, [uniqueCountryIdsForCities])

  const countryById = useMemo(() => {
    const map: Record<number, CountryLookup> = {}
    countries.forEach((c) => {
      if (c?.id) map[c.id] = c
    })
    return map
  }, [countries])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [
        r.code,
        r.name,
        r.location,
        r.email,
        r.wareHouseManagerName,
        r.contactNumber,
        String(cityById[r.city]?.lookupNameEn || cityById[r.city]?.lookupNameAr || r.city || ''),
        String(countryById[r.country]?.lookupNameEn || countryById[r.country]?.lookupNameAr || r.country || ''),
      ].some((field) => String(field).toLowerCase().includes(q))
    )
  }, [rows, searchQuery, cityById, countryById])

  const totalCount = filtered.length
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const page = Math.min(pageNumber, totalPages)
  const pageSlice = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return filtered.slice(start, start + PAGE_SIZE)
  }, [filtered, page])

  const handleAdd = () => navigate('/inventory/warehouses/new')

  const handleEdit = (row: Warehouse) => {
    navigate(`/inventory/warehouses/${row.id}/edit`)
  }

  const handleDelete = async (row: Warehouse) => {
    const ok = await showDeleteConfirm(
      row.name,
      t('inventoryWarehouses.confirmDelete', { name: row.name })
    )
    if (!ok) return
    try {
      setDeletingId(row.id)
      await warehouseService.deleteWarehouse(row.id)
      setRows((prev) => prev.filter((r) => r.id !== row.id))
      await showSuccess(t('common.success'), t('inventoryWarehouses.deleted'))
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('inventoryWarehouses.deleteFailed'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleCopy = () => {
    if (filtered.length === 0) {
      toast.error(t('inventoryWarehouses.nothingToCopy'))
      return
    }
    const header = ['Code', 'Name', 'Location', 'Manager', 'Email', 'Contact', 'City', 'Country']
    const lines = filtered.map((r) =>
      [
        r.code,
        r.name,
        r.location,
        r.wareHouseManagerName,
        r.email,
        r.contactNumber,
        cityById[r.city]?.lookupNameEn || cityById[r.city]?.lookupNameAr || String(r.city || ''),
        countryById[r.country]?.lookupNameEn || countryById[r.country]?.lookupNameAr || String(r.country || ''),
      ].join('\t')
    )
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success(t('inventoryWarehouses.copied'))
  }

  const handleExportCSV = () => {
    if (filtered.length === 0) {
      toast.error(t('inventoryWarehouses.nothingToExport'))
      return
    }
    const header = 'Code,Name,Location,Manager,Email,Contact,City,Country'
    const body = filtered
      .map((r) =>
        [
          r.code,
          r.name,
          r.location,
          r.wareHouseManagerName,
          r.email,
          r.contactNumber,
          cityById[r.city]?.lookupNameEn || cityById[r.city]?.lookupNameAr || String(r.city || ''),
          countryById[r.country]?.lookupNameEn || countryById[r.country]?.lookupNameAr || String(r.country || ''),
        ]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `warehouses_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('inventoryWarehouses.exported'))
  }

  const toggleColumn = (key: ColumnKey) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const startIdx = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const endIdx = Math.min(page * PAGE_SIZE, totalCount)
  const visibleCount = Object.values(visibleColumns).filter(Boolean).length

  const th = (labelKey: string) => (
    <TableHead className="h-12 min-w-[88px] bg-primary/5 px-3 text-center text-xs font-bold text-primary sm:px-4 dark:bg-primary/10 dark:text-primary">
      {t(labelKey)}
    </TableHead>
  )

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryWarehouses.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryWarehouses.listSubtitle')}</p>
          </div>
        </div>
        <Button onClick={handleAdd} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          {t('inventoryWarehouses.addWarehouse')}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium">{t('inventoryWarehouses.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('inventoryWarehouses.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setPageNumber(1)
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Columns className="mr-2 h-4 w-4" />
                    {t('inventoryWarehouses.columnVisibility')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{t('inventoryWarehouses.columnsToggle')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {ALL_COLUMNS.map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={visibleColumns[col.key]}
                      onCheckedChange={() => toggleColumn(col.key)}
                    >
                      {t(col.labelKey)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                {t('inventoryWarehouses.copy')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  {visibleColumns.code && th('inventoryWarehouses.columns.code')}
                  {visibleColumns.name && th('inventoryWarehouses.columns.name')}
                  {visibleColumns.location && th('inventoryWarehouses.columns.location')}
                  {visibleColumns.manager && th('inventoryWarehouses.columns.manager')}
                  {visibleColumns.email && th('inventoryWarehouses.columns.email')}
                  {visibleColumns.contact && th('inventoryWarehouses.columns.contact')}
                  {visibleColumns.city && th('inventoryWarehouses.columns.city')}
                  {visibleColumns.country && th('inventoryWarehouses.columns.country')}
                  <TableHead className="h-12 w-[72px] bg-primary/5 px-2 text-center text-xs font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('common.edit')}
                  </TableHead>
                  <TableHead className="h-12 w-[72px] bg-primary/5 px-2 text-center text-xs font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('common.delete')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleCount + 2}
                      className="py-12 text-center text-muted-foreground"
                    >
                      <div className="p-12 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                        <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : pageSlice.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleCount + 2}
                      className="py-12 text-center text-muted-foreground"
                    >
                      {t('inventoryWarehouses.empty')}
                    </TableCell>
                  </TableRow>
                ) : (
                  pageSlice.map((row) => (
                    <TableRow
                      key={row.id}
                      className="border-b transition-colors hover:bg-primary/5 dark:hover:bg-primary/10"
                    >
                      {visibleColumns.code && (
                        <TableCell className="px-3 py-2 text-center text-sm sm:px-4">{row.code}</TableCell>
                      )}
                      {visibleColumns.name && (
                        <TableCell className="px-3 py-2 text-center text-sm font-medium sm:px-4">
                          {row.name}
                        </TableCell>
                      )}
                      {visibleColumns.location && (
                        <TableCell className="px-3 py-2 text-center text-sm sm:px-4">{row.location}</TableCell>
                      )}
                      {visibleColumns.manager && (
                        <TableCell className="px-3 py-2 text-center text-sm sm:px-4">{row.wareHouseManagerName}</TableCell>
                      )}
                      {visibleColumns.email && (
                        <TableCell className="px-3 py-2 text-center text-sm sm:px-4">{row.email}</TableCell>
                      )}
                      {visibleColumns.contact && (
                        <TableCell className="px-3 py-2 text-center text-sm sm:px-4">{row.contactNumber}</TableCell>
                      )}
                      {visibleColumns.city && (
                        <TableCell className="px-3 py-2 text-center text-sm sm:px-4">
                          {cityById[row.city]?.lookupNameEn || cityById[row.city]?.lookupNameAr || String(row.city || '')}
                        </TableCell>
                      )}
                      {visibleColumns.country && (
                        <TableCell className="px-3 py-2 text-center text-sm sm:px-4">
                          {countryById[row.country]?.lookupNameEn ||
                            countryById[row.country]?.lookupNameAr ||
                            String(row.country || '')}
                        </TableCell>
                      )}
                      <TableCell className="px-2 py-2 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleEdit(row)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="px-2 py-2 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                          disabled={deletingId === row.id}
                          onClick={() => void handleDelete(row)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t px-4 py-4 sm:flex-row sm:px-6">
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              {t('inventoryWarehouses.showing', { start: startIdx, end: endIdx, total: totalCount })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-medium">{page}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
